import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { cancelBid, fetchAllBidOffers, setBidAtWork, setBidService, type BidOfferRow } from '../detailApi';
import { CancelBidModal } from './CancelBidModal';
import { bidKey, useBidAction, useBidServices } from './useBidAction';

/** «Сменить статус» for a new bid: moves it to «В работе» for the first service that offered a price. */
export const AtWorkAction = ({ bidId }: { bidId: number }) => {
  // Same query as the offers table (page 1), so it is served from cache.
  const { data } = useBidServices<BidOfferRow>('withCost', bidId, 1);
  const serviceId = data?.rows[0]?.service?.id;
  const atWork = useBidAction<void>(bidId, () => setBidAtWork(bidId, serviceId ?? ''));

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-sm pull-right"
        disabled={!serviceId || atWork.isPending}
        onClick={() => atWork.run()}
      >
        Сменить
      </button>
      {atWork.error && <span className="label label-danger">{atWork.error}</span>}
    </>
  );
};

export const CancelAction = ({ bidId }: { bidId: number }) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancel = useBidAction(
    bidId,
    ({ reasonId, description }: { reasonId: string; description: string }) =>
      cancelBid(bidId, reasonId, description),
    () => setIsOpen(false),
  );

  return (
    <>
      <button type="button" className="btn btn-danger btn-sm" onClick={() => setIsOpen(true)}>
        Отменить заявку
      </button>
      {isOpen && (
        <CancelBidModal
          isSaving={cancel.isPending}
          error={cancel.error}
          onClose={() => {
            cancel.reset();
            setIsOpen(false);
          }}
          onConfirm={(reasonId, description) => cancel.run({ reasonId, description })}
        />
      )}
    </>
  );
};

/** Autopark customers: attach the bid to one of the services that already made an offer. */
export const AttachOfferBlock = ({ bidId }: { bidId: number }) => {
  const [serviceId, setServiceId] = useState('');
  const offers = useQuery({
    queryKey: bidKey(bidId, 'all-offers'),
    queryFn: () => fetchAllBidOffers(bidId),
  });
  const attach = useBidAction(bidId, (id: string) => setBidService(bidId, id));

  const options = (offers.data ?? []).flatMap(({ service }) =>
    service?.id ? [{ value: String(service.id), label: service.name ?? String(service.id) }] : [],
  );

  return (
    <div className="box">
      <div className="box-body form-inline">
        <label className="control-label" style={{ marginRight: 12 }}>
          Предложения
        </label>
        <FilterSelect
          options={options}
          value={serviceId}
          isLoading={offers.isLoading}
          width={300}
          placeholder="Выберите СТО"
          onChange={setServiceId}
        />
        <button
          type="button"
          className="btn btn-success"
          style={{ marginLeft: 12 }}
          disabled={!serviceId || attach.isPending}
          onClick={() => attach.run(serviceId)}
        >
          Прикрепить
        </button>
        {attach.error && <span className="text-red"> {attach.error}</span>}
      </div>
    </div>
  );
};
